/* HOTSTOP B90K — SHAWARMA CUSTOMIZER */
(() => {
  if (window.HotStopShawarmaCustomizer) return;

  const ROOT_SELECTOR = '[data-hotstop-shawarma-customizer]';
  const ADDON_SELECTOR = '[data-hotstop-shawarma-addon]';
  const REMOVE_SELECTOR = '[data-hotstop-shawarma-remove]';

  function formatNaira(cents) {
    const value = Number(cents || 0) / 100;
    return `₦${value.toLocaleString('en-NG', { maximumFractionDigits: 0 })}`;
  }

  function selected(root, selector) {
    return Array.from(root.querySelectorAll(`${selector}:checked`));
  }

  function syncPreparation(root) {
    const input = root.querySelector('[data-hotstop-shawarma-preparation]');
    if (!(input instanceof HTMLInputElement)) return '';

    const labels = selected(root, REMOVE_SELECTOR)
      .map((checkbox) => checkbox.dataset.label || '')
      .filter(Boolean);

    const value = labels.join(', ');
    input.value = value;
    input.disabled = !value;
    return value;
  }

  function syncSummary(root) {
    const summary = root.querySelector('[data-hotstop-shawarma-summary]');
    if (!summary) return;

    const addons = selected(root, ADDON_SELECTOR);
    const removals = selected(root, REMOVE_SELECTOR);
    const count = addons.length + removals.length;
    const extras = addons.reduce((total, checkbox) => total + Number(checkbox.dataset.priceCents || 0), 0);

    if (!count) {
      summary.textContent = 'Optional';
      return;
    }

    summary.textContent = extras > 0
      ? `${count} selected · +${formatNaira(extras)}`
      : `${count} selected`;
  }

  function syncRoot(root) {
    syncPreparation(root);
    syncSummary(root);
    const message = root.querySelector('[data-hotstop-shawarma-message]');
    if (message) message.textContent = '';
  }

  function enforceConflict(root, checkbox) {
    if (!(checkbox instanceof HTMLInputElement) || !checkbox.checked) return;
    const conflict = checkbox.dataset.hotstopConflict;
    if (!conflict) return;

    const peer = root.querySelector(`[data-hotstop-key="${CSS.escape(conflict)}"]`);
    if (peer instanceof HTMLInputElement && peer.checked) peer.checked = false;
  }

  const PROTEIN_FIELDSET_SELECTOR = 'fieldset.variant-option--equal-width-buttons[data-option-id$="-protein"]';
  const PROTEIN_LABEL_SELECTOR = ':scope > .variant-option__button-label';
  const PROTEIN_TEXT_SELECTOR = '.variant-option__button-label__text';

  function syncProteinMarqueeLabel(label) {
    if (!(label instanceof HTMLElement)) return;
    const text = label.querySelector(PROTEIN_TEXT_SELECTOR);
    if (!(text instanceof HTMLElement)) return;

    label.classList.remove('hotstop-protein-label--marquee');
    label.style.removeProperty('--hotstop-protein-pan-start');
    label.style.removeProperty('--hotstop-protein-pan-end');

    // Measure the natural single-line text against the usable button width.
    const availableWidth = Math.max(0, label.clientWidth - 16);
    const textWidth = Math.ceil(text.scrollWidth);
    const overflow = Math.max(0, textWidth - availableWidth);

    if (overflow <= 2) {
      label.removeAttribute('title');
      return;
    }

    const halfOverflow = Math.ceil(overflow / 2);
    label.style.setProperty('--hotstop-protein-pan-start', `${halfOverflow}px`);
    label.style.setProperty('--hotstop-protein-pan-end', `${-halfOverflow}px`);
    label.classList.add('hotstop-protein-label--marquee');
    label.title = (text.textContent || '').trim();
  }

  function syncProteinMarquees(scope = document) {
    const roots = [];

    if (scope instanceof Element && scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));

    roots.forEach((root) => {
      const productDetails = root.closest('.product-details');
      const fieldset = productDetails?.querySelector(PROTEIN_FIELDSET_SELECTOR);
      if (!fieldset) return;
      fieldset.querySelectorAll(PROTEIN_LABEL_SELECTOR).forEach(syncProteinMarqueeLabel);
    });

    document.documentElement.dataset.hotstopB90nProteinMarquee = roots.length ? 'ready' : 'waiting';
  }

  let proteinMarqueeFrame = 0;
  function scheduleProteinMarqueeSync(scope = document) {
    window.cancelAnimationFrame(proteinMarqueeFrame);
    proteinMarqueeFrame = window.requestAnimationFrame(() => syncProteinMarquees(scope));
  }

  const proteinMarqueeObserver = new MutationObserver((mutations) => {
    const relevantInsertion = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => {
      if (!(node instanceof Element)) return false;
      return node.matches(ROOT_SELECTOR) || Boolean(node.querySelector(ROOT_SELECTOR));
    }));

    if (relevantInsertion) scheduleProteinMarqueeSync(document);
  });

  proteinMarqueeObserver.observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('resize', () => scheduleProteinMarqueeSync(document), { passive: true });
  scheduleProteinMarqueeSync(document);

  document.addEventListener('change', (event) => {
    const checkbox = event.target;
    if (!(checkbox instanceof HTMLInputElement)) return;
    if (!checkbox.matches(`${ADDON_SELECTOR}, ${REMOVE_SELECTOR}`)) return;

    const root = checkbox.closest(ROOT_SELECTOR);
    if (!root) return;

    enforceConflict(root, checkbox);
    syncRoot(root);
  });

  function getQuantity(form) {
    const quantity = form.querySelector('input[name="quantity"]');
    return Math.max(1, Number.parseInt(quantity?.value || '1', 10) || 1);
  }

  function getMainProperties(form) {
    const properties = {};
    const data = new FormData(form);

    for (const [name, value] of data.entries()) {
      const match = /^properties\[(.+)\]$/.exec(name);
      if (!match || value === '') continue;
      properties[match[1]] = String(value);
    }

    return properties;
  }

  function cartRoot() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  function cartSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
      const id = component?.dataset?.sectionId;
      if (id && !ids.includes(id)) ids.push(id);
    });
    return ids;
  }

  function extractSections(result) {
    if (result?.sections && typeof result.sections === 'object') return result.sections;
    if (Array.isArray(result?.results)) {
      for (let i = result.results.length - 1; i >= 0; i -= 1) {
        const sections = result.results[i]?.sections;
        if (sections && typeof sections === 'object') return sections;
      }
    }
    return {};
  }

  async function postCartAdd(payload) {
    const response = await fetch(`${cartRoot()}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => null);
    if (!response.ok) {
      const message = data?.description || data?.message || `Cart request failed with status ${response.status}`;
      throw new Error(message);
    }
    return data;
  }

  async function addItems(items) {
    const sections = cartSectionIds();
    const sectionsValue = sections.length ? sections.join(',') : '';

    try {
      const payload = { items };
      if (sectionsValue) payload.sections = sectionsValue;
      return await postCartAdd(payload);
    } catch (bulkError) {
      console.warn('[HotStop B90K] Bulk shawarma add failed; using sequential fallback.', bulkError);
      const results = [];
      for (const item of items) {
        const payload = { ...item };
        if (sectionsValue) payload.sections = sectionsValue;
        results.push(await postCartAdd(payload));
      }
      return { sequential: true, results };
    }
  }

  function cartUpdateDetail({ resource, formComponent, form, itemCount, sections, source, optimistic }) {
    const variantId = form.querySelector('input[name="id"]')?.value || '';
    return {
      resource: resource || {},
      sourceId: formComponent?.id || 'hotstop-shawarma-customizer',
      data: {
        source,
        itemCount: Number(itemCount) || 0,
        productId: String(formComponent?.dataset?.productId || ''),
        variantId: String(variantId),
        sections: sections || {},
        didError: false,
        hotstopShawarmaCustomizer: true,
        optimistic: Boolean(optimistic),
      },
    };
  }

  function emitOptimisticCartUpdate(formComponent, form, result, items) {
    const itemCount = items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
    const detail = cartUpdateDetail({
      resource: result,
      formComponent,
      form,
      itemCount,
      sections: extractSections(result),
      source: 'product-form-component',
      optimistic: true,
    });

    // Dispatch from the product form so the native quick-add dialog receives the
    // same bubbling cart:update signal it normally uses to close after success.
    (formComponent || form).dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail }));
  }

  async function refreshCartInBackground(formComponent, form, result) {
    try {
      const response = await fetch(`${cartRoot()}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) return;

      const cart = await response.json();
      const detail = cartUpdateDetail({
        resource: cart,
        formComponent,
        form,
        itemCount: Number(cart?.item_count || 0),
        sections: extractSections(result),
        source: 'hotstop-shawarma-customizer',
        optimistic: false,
      });
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail }));
      document.documentElement.dataset.hotstopB90kCartCount = String(cart?.item_count || 0);
    } catch (error) {
      console.warn('[HotStop B90K] Background cart refresh failed.', error);
    }
  }

  function setBusy(form, busy) {
    form.dataset.hotstopShawarmaBusy = busy ? 'true' : 'false';
    form.querySelectorAll('button[type="submit"], button[name="add"]').forEach((button) => {
      if (button instanceof HTMLButtonElement) button.disabled = busy;
    });
  }

  function showMessage(root, text) {
    const message = root.querySelector('[data-hotstop-shawarma-message]');
    if (message) message.textContent = text || '';
  }

  async function handleCustomizedSubmit(form, root) {
    if (form.dataset.hotstopShawarmaBusy === 'true') return;

    const variantId = Number(form.querySelector('input[name="id"]')?.value || 0);
    if (!Number.isFinite(variantId) || variantId <= 0) {
      showMessage(root, 'Please choose your shawarma options first.');
      return;
    }

    const quantity = getQuantity(form);
    const addons = selected(root, ADDON_SELECTOR)
      .map((checkbox) => ({
        id: Number(checkbox.dataset.variantId || 0),
        label: checkbox.dataset.label || 'Add-on',
      }))
      .filter((addon) => Number.isFinite(addon.id) && addon.id > 0);

    if (!addons.length) return;

    const groupKey = `hotstop-shawarma-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const mainProperties = {
      ...getMainProperties(form),
      _hotstop_order_group: groupKey,
      _hotstop_shawarma_customizer: 'B90K',
    };
    const parentTitle = root.dataset.productTitle || '';

    const items = [
      { id: variantId, quantity, properties: mainProperties },
      ...addons.map((addon) => ({
        id: addon.id,
        quantity,
        properties: {
          _hotstop_order_group: groupKey,
          _hotstop_parent_product: parentTitle,
          _hotstop_addon_label: addon.label,
          _hotstop_addon_type: 'shawarma-extra',
        },
      })),
    ];

    const formComponent = form.closest('product-form-component');
    setBusy(form, true);
    showMessage(root, '');
    document.documentElement.dataset.hotstopB90kStatus = 'adding';
    document.documentElement.dataset.hotstopB90kItemCount = String(items.length);

    try {
      const result = await addItems(items);
      document.documentElement.dataset.hotstopB90kStatus = 'added';
      emitOptimisticCartUpdate(formComponent, form, result, items);
      refreshCartInBackground(formComponent, form, result);
      window.setTimeout(() => setBusy(form, false), 700);
    } catch (error) {
      console.error('[HotStop B90K] Shawarma customisation add failed.', error);
      document.documentElement.dataset.hotstopB90kStatus = 'error';
      showMessage(root, error.message || 'Unable to add this customised shawarma. Please try again.');
      setBusy(form, false);
    }
  }

  // Capture phase: only take over Horizon's product submit when an "Add more"
  // item is selected. Remove-only requests stay on the native fast path because
  // the synced Preparation request hidden input is submitted as a normal line
  // item property by Horizon.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const root = form.querySelector(ROOT_SELECTOR);
    if (!root) return;

    syncRoot(root);
    if (!root.querySelector(`${ADDON_SELECTOR}:checked`)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    handleCustomizedSubmit(form, root);
  }, true);

  window.HotStopShawarmaCustomizer = {
    sync: syncRoot,
  };

  document.documentElement.dataset.hotstopB90kStatus = 'ready';
})();
